import React, { memo, useEffect, useRef, useMemo, useState } from "react";
import {
  Text,
  View,
  StyleSheet,
  Dimensions,
  PixelRatio,
  Pressable,
} from "react-native";
import { useSelector } from "react-redux";
import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import t from "@constants/localization";
import { colors } from "@constants/theme";
import analytics from "@services/analytics";
import { SHOP_PERFORMANCE_EVENTS } from "@constants/analyticsEventsCT";
import testIds from "@constants/testIds";
import RenderItem from "./RenderItem";
import RenderCustomer from "./RenderCustomer";
import RenderProduct from "./RenderProduct";

const Tab = createMaterialTopTabNavigator();

const PATH = "screens/ShopPerformance/TabView";

function normalize(size) {
  const newSize = size * scale;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

const { width } = Dimensions.get("window");
const scale = width / 360;

const TabViewComponent = (props) => {
  const {
    customerDetails: customers,
    productDetails: products,
    itemDetails: items,
  } = useSelector(({ shopPerformance }) => shopPerformance);
  const { setSaleCard, setCommissionCard } = props;
  const count = useMemo(
    () => [customers.length, products.length, items.length],
    [customers.length, items.length, products.length],
  );
  const { currentRouteName = "" } = useSelector((state) => state.navigation);
  const [switchedBy, setSwitchedBy] = useState("swipe");

  const refs = {
    ref1: useRef(),
    ref2: useRef(),
    ref3: useRef(),
  };

  const Tabs = [
    {
      title: t(`${PATH}/Customers`),
      name: "CX",
      screen: RenderCustomer,
    },
    {
      title: t(`${PATH}/ProductType`),
      name: "P-type",
      screen: RenderProduct,
    },
    {
      title: t(`${PATH}/Items`),
      name: "SKU",
      screen: RenderItem,
    },
  ];

  const setRef = (key, ref) => {
    if (ref) {
      refs[key].current = ref;
    }
  };

  const getBadgeCount = useMemo(
    () =>
      function (i) {
        return i === 0
          ? count[0] <= 99
            ? count[0]
            : "99+"
          : i === 1
            ? count[1] <= 99
              ? count[1]
              : "99+"
            : i === 2
              ? count[2] <= 99
                ? count[2]
                : "99+"
              : null;
      },
    [count],
  );
  const handleEvent = () => {
    if (
      currentRouteName === "CX" ||
      currentRouteName === "P-type" ||
      currentRouteName === "SKU"
    ) {
      setSaleCard(false);
      setCommissionCard(false);
      if (currentRouteName === "CX") {
        refs.ref2?.current?.scrollToOffset({
          offset: 0,
          animated: false,
        });
        refs.ref3?.current?.scrollToOffset({
          offset: 0,
          animated: false,
        });
      } else if (currentRouteName === "P-type") {
        refs.ref1?.current?.scrollToOffset({
          offset: 0,
          animated: false,
        });
        refs.ref3?.current?.scrollToOffset({
          offset: 0,
          animated: false,
        });
      } else {
        refs.ref2?.current?.scrollToOffset({
          offset: 0,
          animated: false,
        });
        refs.ref1?.current?.scrollToOffset({
          offset: 0,
          animated: false,
        });
      }
      setTimeout(() => {
        analytics.trackEvent(
          SHOP_PERFORMANCE_EVENTS.SHOP_PERFORMANCE_TAB_VIEWED,
          {
            tab_name: currentRouteName,
            type: switchedBy,
          },
        );
        setSwitchedBy("swipe");
      }, 200);
    }
  };

  useEffect(() => {
    handleEvent();
  }, [currentRouteName]);

  const TabButton = memo(({ focused, i, tab }) => {
    return (
      <Pressable
        android_ripple={{ color: "rgba(0,0,0,0.08)", borderless: true }}
        style={{
          flexDirection: "row",
        }}
      >
        <Text
          style={[
            styles.headerText,
            {
              color: focused ? colors.primary : colors.black,
              fontFamily: focused
                ? "NotoSansDevanagari-Bold"
                : "NotoSansDevanagari-Medium",
            },
          ]}
        >
          {tab.title}
        </Text>
        <View
          style={[
            styles.badge,
            {
              backgroundColor: focused ? colors.primary : "rgba(0,0,0,0.056)",
              alignItems: "center",
            },
          ]}
        >
          <Text
            style={[
              styles.badgeText,
              {
                color: focused ? colors.white : "rgba(0,0,0,0.56)",
              },
            ]}
          >
            {getBadgeCount(i)}
          </Text>
        </View>
      </Pressable>
    );
  });

  return (
    <View style={{ flex: 1, zIndex: -10 }}>
      <Tab.Navigator
        backBehavior="none"
        screenOptions={({ route }) => ({
          tabBarActiveTintColor: "#ffffff",
          tabBarIndicatorStyle: { backgroundColor: colors.primary, height: 3 },
          tabBarContentContainerStyle: { padding: 4 },
          lazy: true,
        })}
      >
        {Tabs.map((tab, i) => {
          return (
            <Tab.Screen
              testID={`${i}-${testIds.tab}`}
              key={tab.name}
              options={{
                tabBarLabel: ({ focused }) => (
                  <TabButton focused={focused} i={i} tab={tab} />
                ),
              }}
              name={tab.name}
              children={() => (
                <tab.screen
                  {...props}
                  setCommissionCard={setCommissionCard}
                  setSaleCard={setSaleCard}
                  setRef={setRef}
                />
              )}
              listeners={() => ({
                tabPress: () => {
                  setSwitchedBy("click");
                },
              })}
            />
          );
        })}
      </Tab.Navigator>
    </View>
  );
};

const styles = StyleSheet.create({
  headerText: {
    fontSize: normalize(16),
    fontFamily: "NotoSansDevanagari-Regular",
    textAlign: "center",
  },
  badge: {
    width: normalize(28),
    height: normalize(28),
    backgroundColor: "rgba(0,0,0,0.05)",
    borderRadius: 40,
    justifyContent: "center",
    alignItems: "center",
    marginLeft: 5,
  },
  badgeText: {
    fontSize: 12,
    width: normalize(22),
    textAlign: "center",
    color: "rgba(0,0,0,0.56)",
    fontFamily: "NotoSansDevanagari-SemiBold",
  },
  NotFountView: { flex: 1, justifyContent: "center" },
  NotFoundText: {
    marginTop: normalize(16),
    fontSize: normalize(16),
    lineHeight: normalize(26),
    fontFamily: "NotoSansDevanagari-SemiBold",
    textAlign: "center",
  },
});

export default TabViewComponent;
